import React, { useState } from 'react';
import PropTypes from 'prop-types';
import AgreementLanguageToolbar from './AgreementLanguageToolbar';
import AgreementBodyView from './AgreementBodyView';
import {
  AGREEMENT_DEFAULT_LANGUAGE,
  resolveAgreementDocument,
} from '../../lib/agreementDocuments';

/**
 * Language-aware agreement viewer used by Confidentiality and AML steps.
 * Acceptance checkbox stays outside this panel (caller-owned).
 */
export default function AgreementDocumentPanel({
  agreementKey,
  title,
  subtitle,
  acceptedAt,
  showDownload = true,
  initialLanguage = AGREEMENT_DEFAULT_LANGUAGE,
}) {
  const [language, setLanguage] = useState(initialLanguage);
  const resolved = resolveAgreementDocument(agreementKey, language);
  const bodyLanguage =
    resolved.status === 'available'
      ? resolved.displayLanguage
      : resolved.showFallbackContent
        ? resolved.fallbackLanguage
        : null;

  return (
    <div className="space-y-4" data-testid={`agreement-panel-${agreementKey}`}>
      <div>
        <h2 className="font-display text-lg font-semibold text-slate-900">{title}</h2>
        {subtitle ? <p className="text-sm text-slate-500 mt-1">{subtitle}</p> : null}
      </div>

      <AgreementLanguageToolbar
        agreementKey={agreementKey}
        language={language}
        onLanguageChange={setLanguage}
        showDownload={showDownload}
      />

      <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white shadow-sm">
        <div className="overflow-y-auto" style={{ height: '400px' }}>
          {bodyLanguage ? (
            <>
              {resolved.status === 'unavailable' && resolved.showFallbackContent ? (
                <div className="px-4 pt-3">
                  <p className="text-[11px] font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 inline-block">
                    Fallback document language: {resolved.fallbackLabel} (approved)
                  </p>
                </div>
              ) : null}
              <AgreementBodyView
                agreementKey={agreementKey}
                languageCode={bodyLanguage}
                acceptedAt={acceptedAt}
              />
            </>
          ) : (
            <div className="flex h-full items-center justify-center p-6 text-sm text-slate-500 text-center">
              No approved document is available for the selected language.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

AgreementDocumentPanel.propTypes = {
  agreementKey: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  acceptedAt: PropTypes.string,
  showDownload: PropTypes.bool,
  initialLanguage: PropTypes.string,
};
